import { Component, ElementRef, Inject, Injector, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CTCustomizableChartComponent } from '@cloud-twin-v2/cloud-twin-shared/chart/ct-customizable-chart/ct-customizable-chart.component';
import { CTChartConfigurationService } from '@cloud-twin-v2/cloud-twin-shared/chart/services/ct-chart-configuration.service';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import { DashboardCartAndSettings } from '@cloud-twin-v2/cloud-twin-shared/filtrable-items/dashboard-cart-and-settings';
import { ChartWidgetSettings } from '@cloud-twin-v2/cloud-twin-shared/filtrable-items/widget-settings';
import { WidgetSettingsToken } from '@common-modules/dynamic-layout/dynamic-layout.tokens';
import { GetValueSelector } from '@common-modules/dynamic-layout/state/generic/generic.selectors';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { DashboardChildWidgetParams } from '@common-modules/right-panel/dashboards/dashboard-widget-params';
import { HorizontalAxis } from '@common-modules/shared/charts/model/axes/horizontal-axis';
import { VerticalAxis } from '@common-modules/shared/charts/model/axes/vertical-axis';
import { ChartConfiguration } from '@common-modules/shared/charts/model/chart/chart-configuration';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { IElementSize } from '@common-modules/shared/model/element-size';
import { SizeCalculatorService } from '@common-modules/shared/services/size-calculator.service';
import { GenericMapDataService } from '@common-modules/wlm-map/generic-map/services/generic-map-data.service';
import { DataVisualizationStateFields } from '@water-loss/features/data-visualization/models/data-visualization-state-fields';
import { plainToClass } from 'class-transformer';
import { Observable } from 'rxjs';
import { BaseDashboardWidgetComponent } from '../core/base-dashboard-widget-component';

const COMPONENT_SELECTOR = 'ct-dashboard-chart-widget';

@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './dashboard-chart-widget.component.html',
  styleUrl: './dashboard-chart-widget.component.scss',
  standalone: false,
})
export class DashboardChartWidgetComponent extends BaseDashboardWidgetComponent implements OnInit {
  @ViewChild(CTCustomizableChartComponent) customizableChart: CTCustomizableChartComponent;
  readonly T_SCOPE = `${TranslateScopes.Dashboard}.${COMPONENT_SELECTOR}`;

  chartConfiguration: ChartConfiguration;

  phTitleKey = `${this.T_SCOPE}.must-plot-title`;
  phSubtitleKey = `${this.T_SCOPE}.must-plot-subtitle`;
  size$: Observable<IElementSize>;
  currentTimeStep: Date;

  private _currentWidgetSettings: StateWidgetSettings;

  get componentName(): string {
    return 'DashboardChartWidgetComponent';
  }

  constructor(
    injector: Injector,
    @Inject(WidgetSettingsToken) widgetSettings: StateWidgetSettings,
    private _chartConfigurationService: CTChartConfigurationService,
    private readonly _element: ElementRef,
    private readonly _sizeCalculatorService: SizeCalculatorService,
    private readonly _genericMapDataService: GenericMapDataService
  ) {
    super(injector, widgetSettings);

    this._currentWidgetSettings = widgetSettings;
    this.setChartConfiguration(widgetSettings.itemSettings?.params);
  }

  onWidgetInit(): void {
    this.listenSettingsChange();
    this.size$ = this._sizeCalculatorService.listenElementSize$(this._element.nativeElement);

    this.listenState(DataVisualizationStateFields.plotChartConfiguration).subscribe(
      (chartConfiguration: ChartConfiguration) => {
        if (chartConfiguration) {
          this.chartConfiguration = globalUtilsHelper.clone(chartConfiguration);
        }
      }
    );

    this.listenState(DataVisualizationStateFields.clearAll).subscribe((value) => {
      if (value) {
        this.chartConfiguration = null;
      }
    });

    this.listenMapTimeStep();
    this.listenTimeBarDateRange();
  }

  onChartConfigurationChange(chartConfiguration: ChartConfiguration) {
    const setting = globalUtilsHelper.clone(
      this._widgetSettings.itemSettings?.params
    ) as DashboardCartAndSettings;
    const chartWidgetSettings = new ChartWidgetSettings();
    chartWidgetSettings.chartConfiguration = chartConfiguration;
    setting.widgetSettings = chartWidgetSettings;

    this.updateItemSettings({
      title: this.chartConfiguration.customization.name,
      params: setting,
      widgetInstanceKey: this.getWidgetInstanceKey(),
    });
  }

  reload(updatedParams: DashboardCartAndSettings) {
    if (
      updatedParams.widgetSettings &&
      'chartConfiguration' in updatedParams.widgetSettings &&
      updatedParams.widgetSettings.chartConfiguration
    ) {
      const config = updatedParams.widgetSettings.chartConfiguration as ChartConfiguration;

      const widgetSettings = new ChartWidgetSettings();
      widgetSettings.chartConfiguration = config;
      updatedParams.widgetSettings = widgetSettings;
      if (this._widgetSettings.itemSettings) {
        this._widgetSettings.itemSettings.params = updatedParams;
      }
      this.setChartConfiguration(updatedParams);
    }
  }

  private listenState(fieldName: string): Observable<any> {
    return this._state
      .select(
        new GetValueSelector({
          fieldName,
        })
      )
      .pipe(takeUntilDestroyed(this._destroyRef));
  }

  private listenTimeBarDateRange(): void {
    this._genericMapDataService
      .listenCurrentDateRange$()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((dateRange) => {
        if (!dateRange) {
          return;
        }
        this.customizableChart?.setDateParams(dateRange.start, dateRange.end);
      });
  }

  private listenMapTimeStep() {
    this._genericMapDataService
      .listenCurrentTimeStep$()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((step) => {
        if (!step) {
          return;
        }
        this.currentTimeStep = step;
      });
  }

  private setChartConfiguration(params: DashboardChildWidgetParams) {
    if (!params) {
      return;
    }

    const dashboardCart: DashboardCartAndSettings = plainToClass(DashboardCartAndSettings, params);

    if (dashboardCart?.widgetSettings) {
      this.rebuildInstances(dashboardCart);
    }

    const { start, end } = this.getIntervalDates(new Date(dashboardCart.timeStep));

    dashboardCart.items.forEach((item) => {
      item.startDate = start;
      item.endDate = end;
    });

    dashboardCart.visibleItems.forEach((item) => {
      item.startDate = start;
      item.endDate = end;
    });

    if (dashboardCart.widgetSettings instanceof ChartWidgetSettings) {
      const chartConfig = (dashboardCart.widgetSettings as ChartWidgetSettings).chartConfiguration;
      if (chartConfig && chartConfig.seriesConfiguration) {
        chartConfig.seriesConfiguration.forEach((serie) => {
          serie.serieDefinition.startDate = start;
          serie.serieDefinition.endDate = end;
        });
      }

      if (chartConfig && chartConfig.horizontalAxis) {
        chartConfig.horizontalAxis.forEach((axis) => {
          axis.minimum = start;
          axis.maximum = end;
        });
      }

      this.chartConfiguration = chartConfig;
    } else {
      if (dashboardCart.visibleItems.length) {
        this._chartConfigurationService
          .getChartConfigurationFromFiltrableItems(dashboardCart.visibleItems)
          .subscribe({
            next: (chartConfiguration: ChartConfiguration) => {
              this.chartConfiguration = chartConfiguration;
              this.chartConfiguration.customization.name = this._settings.title;
            },
            error: (error: any) => {},
          });
      }
    }
  }

  private rebuildInstances(dashboardCart: DashboardCartAndSettings) {
    const widgetSettingsInstance = plainToClass(ChartWidgetSettings, dashboardCart.widgetSettings);
    const vAxisList = [];
    widgetSettingsInstance.chartConfiguration.verticalAxis.forEach((axis) => {
      vAxisList.push(plainToClass(VerticalAxis, axis));
    });

    widgetSettingsInstance.chartConfiguration.verticalAxis = vAxisList;

    const hAxisList = [];
    widgetSettingsInstance.chartConfiguration.horizontalAxis.forEach((axis) => {
      hAxisList.push(plainToClass(HorizontalAxis, axis));
    });

    widgetSettingsInstance.chartConfiguration.horizontalAxis = hAxisList;

    dashboardCart.widgetSettings = widgetSettingsInstance;
  }

  private getIntervalDates(step: Date) {
    const start = new Date(step);
    const end = new Date(step);
    start.setDate(step.getDate() - 7);
    end.setDate(step.getDate() + 7);
    return { start, end };
  }
}
